import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const SEOPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            SEO Services
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Dominate search results and attract more customers with our proven SEO strategies that deliver real results.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            SEO Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Increase Visibility</h3>
              <p className="text-gray-600">Get found by customers searching for your products and services online.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Drive Quality Traffic</h3>
              <p className="text-gray-600">Attract visitors who are actively looking for what you offer.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Cost-Effective Marketing</h3>
              <p className="text-gray-600">Long-term results that provide ongoing value without constant ad spend.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Our SEO Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-green-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-search-2-line text-3xl text-green-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Keyword Research</h4>
              <p className="text-sm text-gray-600">Find the best keywords for your business</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-6 rounded-xl">
              <i className="ri-file-edit-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Content Optimization</h4>
              <p className="text-sm text-gray-600">Optimize your content for search engines</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-links-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Link Building</h4>
              <p className="text-sm text-gray-600">Build authority with quality backlinks</p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-red-100 p-6 rounded-xl">
              <i className="ri-settings-3-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Technical SEO</h4>
              <p className="text-sm text-gray-600">Optimize site speed and technical elements</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-bar-chart-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Analytics & Reporting</h4>
              <p className="text-sm text-gray-600">Track progress with detailed reports</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-map-pin-line text-3xl text-orange-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Local SEO</h4>
              <p className="text-sm text-gray-600">Dominate local search results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Connect With Our SEO Experts</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Rank Higher?</h2>
          <p className="text-xl mb-8">Let's boost your search rankings and drive more organic traffic!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-blue-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SEOPage;